import React, { useEffect, useState } from "react";
import { Bell, Mail, Phone, MessageSquare, AlertCircle, Save, Smartphone } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import usePushSubscription from "../hooks/usePushSubscription";

export default function NotificationSettings() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/notifications/prefs"); setPrefs(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  if (!prefs) return <div className="pt-32 text-center text-white/50">Loading…</div>;

  const update = (patch) => setPrefs({ ...prefs, ...patch });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/notifications/prefs", prefs);
      toast.success("Preferences saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const Row = ({ testid, label, desc, value, onChange, disabled }) => (
    <label className={`flex items-start gap-4 p-4 rounded-lg border border-white/10 bg-white/[0.02] ${disabled ? "opacity-60" : "hover:bg-white/5 cursor-pointer"}`}>
      <input
        type="checkbox"
        data-testid={testid}
        checked={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 w-5 h-5 accent-tv-accent"
      />
      <div className="flex-1">
        <div className="text-white font-semibold text-sm">{label}</div>
        <div className="text-white/60 text-xs mt-0.5">{desc}</div>
      </div>
    </label>
  );

  return (
    <div data-testid="notification-settings-page" className="pt-32 pb-20 max-w-3xl mx-auto px-4 sm:px-6 md:px-8">
      <div className="text-[10px] uppercase tracking-[0.3em] text-tv-accent font-bold flex items-center gap-2">
        <Bell className="w-3.5 h-3.5" /> Notifications
      </div>
      <h1 className="font-display font-black text-4xl sm:text-5xl text-white tracking-tight mt-1">How should we reach you?</h1>
      <p className="text-white/60 mt-2 text-sm">Tap IN TV will respect your choices across in-app, email, and SMS channels.</p>

      <div className="mt-8 space-y-6">
        <section>
          <div className="text-xs uppercase tracking-widest text-white/50 font-bold mb-2 flex items-center gap-2">
            <Mail className="w-3 h-3" /> Email
          </div>
          <div className="space-y-2">
            <Row testid="prefs-email-activity" label="Activity emails" desc="Likes, comments, new followers, creator uploads from channels you follow." value={prefs.email_activity} onChange={(v) => update({ email_activity: v })} />
            <Row testid="prefs-email-marketing" label="Marketing emails" desc="Promotions, creator blasts, subscription deals and launch announcements." value={prefs.email_marketing} onChange={(v) => update({ email_marketing: v })} />
          </div>
        </section>

        <section>
          <div className="text-xs uppercase tracking-widest text-white/50 font-bold mb-2 flex items-center gap-2">
            <Phone className="w-3 h-3" /> SMS
          </div>
          {!prefs.sms_configured && (
            <div className="flex gap-2 items-start text-xs text-yellow-300 bg-yellow-400/10 border border-yellow-400/20 rounded-lg px-3 py-2 mb-3" data-testid="prefs-sms-not-configured">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>SMS sending isn't active yet. Admin can enable it by adding Twilio credentials. Your preference will be remembered.</div>
            </div>
          )}
          <div className="mb-3">
            <label className="block text-xs uppercase tracking-wider text-white/50 font-bold mb-1">Phone number</label>
            <input
              data-testid="prefs-phone"
              value={prefs.phone || ""}
              onChange={(e) => update({ phone: e.target.value })}
              placeholder="+1 555 555 5555"
              className="w-full sm:w-80 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
            />
          </div>
          <div className="space-y-2">
            <Row testid="prefs-sms-activity" label="Activity SMS" desc="Time-sensitive alerts (e.g. live stream starts, kid upload needs parent approval)." value={prefs.sms_activity} onChange={(v) => update({ sms_activity: v })} />
            <Row testid="prefs-sms-marketing" label="Marketing SMS" desc="Creator blasts and Tap IN TV promotions. Standard carrier rates apply." value={prefs.sms_marketing} onChange={(v) => update({ sms_marketing: v })} />
          </div>
        </section>

        <section>
          <div className="text-xs uppercase tracking-widest text-white/50 font-bold mb-2 flex items-center gap-2">
            <MessageSquare className="w-3 h-3" /> In-app
          </div>
          <Row testid="prefs-push" label="Notification bell" desc="Show alerts in the bell at the top of the page." value={prefs.push} onChange={(v) => update({ push: v })} />
          <Row testid="prefs-notify-on-live-follows" label="Live alerts from creators you follow" desc="Get a 🔴 LIVE notification the moment a creator you follow goes live." value={prefs.notify_on_live_follows !== false} onChange={(v) => update({ notify_on_live_follows: v })} />
        </section>

        <section>
          <div className="text-xs uppercase tracking-widest text-white/50 font-bold mb-2 flex items-center gap-2">
            <Smartphone className="w-3 h-3" /> Native push (browser / OS)
          </div>
          <NativePushRow />
        </section>
      </div>

      <div className="mt-8 flex justify-end">
        <button
          data-testid="prefs-save-btn"
          onClick={save}
          disabled={saving}
          className="bg-tv-accent hover:bg-yellow-400 disabled:opacity-50 text-black font-bold px-6 py-3 rounded-full flex items-center gap-2"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save preferences"}
        </button>
      </div>
    </div>
  );
}

/* ─── Native push (Web Push API) ─────────────────────────────────────── */
function NativePushRow() {
  const { status, supported, busy, subscribe, unsubscribe, testPush } = usePushSubscription();

  const isSubscribed = status === "subscribed";
  const isDenied = status === "denied";
  const desc =
    !supported ? "Your browser doesn't support push notifications yet."
    : isDenied ? "Push permission was blocked in your browser. Re-enable it in your site settings, then come back."
    : isSubscribed ? "Native notifications are ON for this device. We'll ping you the moment a creator you follow goes live."
    : "Allow Tap IN to send you native notifications even when this tab isn't open.";

  return (
    <div data-testid="prefs-native-push" className={`p-4 rounded-lg border ${isSubscribed ? "border-emerald-500/30 bg-emerald-500/[0.04]" : "border-white/10 bg-white/[0.02]"}`}>
      <div className="flex items-start gap-4">
        <div className={`w-5 h-5 rounded-md mt-1 flex items-center justify-center ${isSubscribed ? "bg-emerald-500" : "bg-white/10"}`}>
          {isSubscribed ? <span className="text-white text-[10px] font-black">✓</span> : <Bell className="w-3 h-3 text-white/40" />}
        </div>
        <div className="flex-1">
          <div className="text-white font-semibold text-sm">
            Native push on this device
            {isSubscribed && <span className="ml-2 text-emerald-400 text-[10px] font-black uppercase tracking-wider">Active</span>}
          </div>
          <div className="text-white/60 text-xs mt-0.5">{desc}</div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {isSubscribed ? (
              <>
                <button
                  data-testid="prefs-push-test"
                  onClick={async () => {
                    const r = await testPush();
                    if (r?.sent > 0) toast.success(`Sent ${r.sent} test push${r.sent === 1 ? "" : "es"}`);
                    else toast.error("No active subscription found");
                  }}
                  className="bg-white/10 hover:bg-white/15 text-white text-xs font-bold px-3 py-1.5 rounded-full"
                >
                  Send test
                </button>
                <button
                  data-testid="prefs-push-unsubscribe"
                  onClick={async () => { const r = await unsubscribe(); if (r.ok) toast.success("Native push disabled"); }}
                  disabled={busy}
                  className="bg-white/10 hover:bg-white/15 text-white text-xs font-bold px-3 py-1.5 rounded-full"
                >
                  Disable
                </button>
              </>
            ) : (
              <button
                data-testid="prefs-push-subscribe"
                onClick={async () => {
                  const r = await subscribe();
                  if (r.ok) toast.success("🎉 Native push enabled — try Send test");
                  else if (r.reason === "denied") toast.error("Permission was blocked in your browser settings");
                  else if (r.reason === "unsupported") toast.error("Your browser doesn't support push");
                }}
                disabled={busy || !supported || isDenied}
                className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-400 hover:to-rose-500 disabled:opacity-50 text-white text-xs font-black uppercase tracking-wider px-4 py-1.5 rounded-full"
              >
                Enable native push
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

